import { useLocation } from 'react-router-dom';
import { FLOW_ICONS } from '../icons';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuItemIcon,
} from '@clay-run/glaze/sidebar-next/index';
import clayLogo from '../../../assets/clay-logo.png';
import { openPanel } from './panels/flow-panels-store';
import RailAccountMenu from './RailAccountMenu';
import { UiViewChip } from '../flow-layouts/UiViewChip';
import { useMediaQuery } from '../useMediaQuery';
import MobileRailMenu from './rail/MobileRailMenu';
import { isActiveKey, useRailItems, useRailNavigate, type RailOverrides } from './rail/rail-items';

/** Below this width the rail is a floating hamburger (the old rail's breakpoint). */
const MOBILE_RAIL_QUERY = '(max-width: 767px)';

/**
 * The new shell's rail, rendered from `rail/rail-items.ts`'s spec. It keeps
 * hb `shell/NavRail.tsx`'s composition (`Sidebar` as the icon strip, no control
 * to widen it, footer order Apps · Chat · User Role chip · avatar), but every item is WIRED to a
 * real Terra affordance (`/terra-design-v2` build rule: "a control is wired or
 * absent"). The avatar and its menu are `RailAccountMenu`. No rail gear (plan
 * 005 §0.2): Preferences opens only from the avatar menu.
 *
 * The main list is the spec filtered by user type, manager flag and the
 * manifest's `hideNavKeys` (see `useRailItems`). The active item comes from the
 * pathname (`isActiveKey`); admin, `/tools/*`, `/artifact/*` and unknown routes
 * light none. Chat opens the chat panel on every route. Apps navigates to
 * `/tools` through `go` (which reopens Flow, like the mobile row) and is never active.
 * The rail draws the User Role chip (`UiViewChip`) just above the avatar, naming
 * the team whose defaults the rep sees; it renders nothing until the view
 * preferences have loaded. It is the one role switcher here: the avatar menu has
 * no User Role row. Picking the rep's own team clears the lens. The phone
 * hamburger has no chip, so `MobileRailMenu` keeps its own User Role group.
 *
 * Below 768px the strip is replaced by `MobileRailMenu`, a floating hamburger
 * with the same items plus Apps, Chat and the account group. `V2Shell` drops
 * the rail column there.
 *
 * `V2Shell` pins its `SidebarProvider` closed, so the rail is always the icon
 * strip and its width is set there. Each item keeps its label in a `<span>`,
 * which Glaze clips in the icon strip; Chat carries an `aria-label`.
 */
function NavRail({ overrides = {} }: { overrides?: RailOverrides }) {
  const { pathname } = useLocation();
  const items = useRailItems(overrides);
  const { go, goFlow, goItem } = useRailNavigate();
  const activeKey = isActiveKey(pathname, items);
  const isMobile = useMediaQuery(MOBILE_RAIL_QUERY);

  if (isMobile) return <MobileRailMenu items={items} activeKey={activeKey} overrides={overrides} />;

  return (
    <Sidebar
      collapsible="icon"
      variant="sidebar"
      className="border-r-[0.5px]! border-border-primary!"
    >
      {!overrides.hideLogo && (
        <SidebarHeader className="h-12 items-center justify-center border-b-[0.5px]! border-border-primary!">
          <button
            type="button"
            onClick={goFlow}
            aria-label="Terra Flow"
            className="flex size-[32px] shrink-0 items-center justify-center rounded-md transition-colors hover:bg-bg-secondary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus-ring"
          >
            <img src={clayLogo} alt="" aria-hidden className="size-5 object-contain" />
          </button>
        </SidebarHeader>
      )}
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu className="group-data-[collapsible=icon]:items-center">
            {items.map((item) => (
              <SidebarMenuItem key={item.key} variant="primary">
                <SidebarMenuButton
                  isActive={item.key === activeKey}
                  tooltip={item.label}
                  onClick={() => goItem(item)}
                  data-testid={`nav-rail-${item.key}`}
                >
                  <SidebarMenuItemIcon type={FLOW_ICONS[item.icon]} />
                  <span>{item.label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      {/* Glaze's footer has no padding and its group has 8px, so the footer
          rows sit in a group to share the content rows' icon column. The
          footer's 12px bottom padding plus the group's 8px is a 20px edge. */}
      <SidebarFooter className="pb-3">
        <SidebarGroup>
          {/* The footer rows use the same item and button as the content rows, so they share its column and hover accent. */}
          <SidebarMenu className="group-data-[collapsible=icon]:items-center">
            {!overrides.hideTools && (
              <SidebarMenuItem variant="primary">
                <SidebarMenuButton tooltip="Apps" onClick={() => go('/tools')}>
                  <SidebarMenuItemIcon type={FLOW_ICONS.apps} />
                  <span>Apps</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            <SidebarMenuItem variant="primary">
              <SidebarMenuButton tooltip="Chat" aria-label="Chat" onClick={() => openPanel('chat')}>
                <SidebarMenuItemIcon type={FLOW_ICONS.chat} />
                <span>Chat</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {!overrides.hideUserMenu && (
              <SidebarMenuItem variant="primary" className="flex flex-col items-center gap-1">
                <UiViewChip />
                <RailAccountMenu />
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarFooter>
    </Sidebar>
  );
}

export default NavRail;
